import { CircleDot, ExternalLink } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { gameLabel, modeLabel } from '../../../constants/modes';
import {
  deriveTournamentStatus,
  getTournamentParticipation,
  isValidExternalUrl,
  toDate,
} from './tournamentHelpers';

const fmtDate = (d) =>
  d
    ? d
        .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
        .toUpperCase()
        .replace(/\./g, '')
    : '—';

const deriveStatus = deriveTournamentStatus;

const statusPillClass = {
  active: 'border-heat/60 text-heat',
  upcoming: 'border-border text-foreground',
  completed: 'border-border/60 text-muted-foreground',
};

const TournamentHeader = ({ tournament }) => {
  const status = deriveStatus(tournament);
  const participation = getTournamentParticipation(tournament, status);
  const streamUrl = isValidExternalUrl(tournament.streamUrl) ? tournament.streamUrl.trim() : null;
  const start = toDate(tournament.startDate);
  const end = toDate(tournament.endDate);

  return (
    <header className="border-b border-border/60">
      {tournament.imageUrl && (
        <div className="relative h-64 w-full overflow-hidden sm:h-96 lg:h-[32rem]">
          <img src={tournament.imageUrl} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/50 to-transparent" />
        </div>
      )}

      <div className="mx-auto max-w-screen-2xl px-4 sm:px-6">
        <div
          className={cn(
            'flex flex-col gap-5 rounded-md border border-border/60 bg-background/70 p-5 backdrop-blur-md sm:flex-row sm:items-center sm:gap-8 sm:p-6',
            tournament.imageUrl ? 'relative -mt-14 sm:-mt-16' : 'mt-10'
          )}
        >
          {tournament.tournamentLogo && (
            <img
              src={tournament.tournamentLogo}
              alt={`${tournament.name} logo`}
              className="h-20 w-20 shrink-0 rounded-md border border-border/60 bg-background/60 object-contain p-2 sm:h-28 sm:w-28"
            />
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-3 flex flex-wrap items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <span className="inline-flex items-center gap-2">
                <span className="h-px w-6 bg-heat" />
                Tournament
              </span>
              <span className="text-foreground/30">·</span>
              <span
                className={cn(
                  'inline-flex items-center gap-1.5 border px-2 py-0.5',
                  statusPillClass[status] || statusPillClass.upcoming
                )}
              >
                {status === 'active' && <CircleDot className="h-2.5 w-2.5" />}
                {status}
              </span>
              {tournament.game && (
                <>
                  <span className="text-foreground/30">·</span>
                  <span>{gameLabel(tournament.game)}</span>
                </>
              )}
              {tournament.mode && (
                <>
                  <span className="text-foreground/30">·</span>
                  <span>Mode · {modeLabel(tournament.mode)}</span>
                </>
              )}
            </div>

            <h1 className="truncate font-display text-3xl font-bold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
              {tournament.name}
            </h1>

            <div className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground tabular-nums">
              {fmtDate(start)} <span className="mx-2 text-foreground/30">→</span> {fmtDate(end)}
            </div>
            {(streamUrl || participation) && (
              <div className="mt-4 flex flex-wrap gap-2">
                {streamUrl && (
                  <a
                    href={streamUrl}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex w-fit items-center gap-2 rounded-md border border-heat/60 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-heat transition-colors hover:bg-heat hover:text-heat-foreground"
                  >
                    Watch stream
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
                {participation && (
                  <a
                    href={participation.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex w-fit items-center gap-2 rounded-md border border-heat/60 px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-heat transition-colors hover:bg-heat hover:text-heat-foreground"
                  >
                    Participate
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="py-8">
          {tournament.description && (
            <p className="max-w-3xl text-sm text-muted-foreground sm:text-base">
              {tournament.description}
            </p>
          )}
        </div>
      </div>
    </header>
  );
};

export { TournamentHeader, deriveStatus, toDate, fmtDate };
export default TournamentHeader;
